import { useState, useEffect, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import PageSEO from "@/components/PageSEO";
import ShareButtons from "@/components/ShareButtons";
import { homestayAPI, reviewAPI, inquiryAPI, placeAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowLeft,
  ArrowRight,
  Star,
  MapPin,
  Phone,
  Globe,
  Utensils,
  Coffee,
  Calendar as CalendarIcon,
  Users,
  Send,
  Loader2,
  Play,
  X,
  Clock,
  Wifi,
  Car,
  Waves,
  Mountain,
  TreePine,
  Home,
  Bed,
  Bath,
  Wind,
  Tv,
  UtensilsCrossed,
  Expand,
  ExternalLink,
  Compass,
  Images,
  ChevronRight,
  Heart,
  Navigation,
  Share2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Track call button clicks (fire-and-forget)
const trackCallClick = (homestayId) => {
  if (homestayId) {
    fetch(`${BACKEND_URL}/api/homestays/${homestayId}/track-call`, { method: "POST" }).catch(() => {});
  }
};

// Facility icon mapping
const facilityIcons = {
  "WiFi": Wifi,
  "Parking": Car,
  "Swimming Pool": Waves,
  "Mountain View": Mountain,
  "Garden": TreePine,
  "Kitchen": UtensilsCrossed,
  "AC": Wind,
  "TV": Tv,
  "Hot Water": Bath,
  "Room Service": Bed,
  "default": Home,
};

const ListingDetailPage = () => {
  const { id, slug } = useParams();
  const location = useLocation();
  const [homestay, setHomestay] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  
  // Track if view has been recorded to prevent duplicate counts
  const viewTrackedRef = useRef(false);

  // Inquiry form state
  const [inquiryForm, setInquiryForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    guests: 2,
  });
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [submittingInquiry, setSubmittingInquiry] = useState(false);

  // Review form state
  const [reviewForm, setReviewForm] = useState({
    reviewer_name: "",
    reviewer_email: "",
    rating: 5,
    comment: "",
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  
  // Nearby places state
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);

  useEffect(() => {
    fetchHomestay();
  }, [id, slug]);

  useEffect(() => {
    if (homestay) {
      fetchReviews();
      if (homestay.latitude && homestay.longitude) {
        fetchNearbyPlaces();
      }
    }
  }, [homestay]);

  // SEO: Update meta tags and inject Schema.org dynamically
  useEffect(() => {
    if (!homestay?.name) return;
    
    const currentUrl = window.location.href;
    const displayImages = homestay.images?.length > 0
      ? homestay.images
      : ["https://images.unsplash.com/photo-1664616466598-1f1a394f9009?w=1200&h=800&fit=crop"];
    const ogImage = displayImages[0]?.startsWith('http') 
      ? displayImages[0] 
      : `${process.env.REACT_APP_BACKEND_URL}${displayImages[0]}`;
    
    document.title = homestay.meta_title || `${homestay.name}`;
    
    const updateMeta = (selector, content) => {
      let meta = document.querySelector(selector);
      if (meta) meta.content = content;
    };
    
    updateMeta('meta[name="description"]', homestay.meta_description || homestay.description || `Book ${homestay.name}, a beautiful homestay.`);
    updateMeta('meta[property="og:title"]', homestay.meta_title || `${homestay.name}`);
    updateMeta('meta[property="og:description"]', homestay.meta_description || homestay.description || `Book ${homestay.name}`);
    updateMeta('meta[property="og:image"]', ogImage);
    updateMeta('meta[property="og:url"]', currentUrl);
    updateMeta('meta[name="twitter:title"]', homestay.meta_title || `${homestay.name}`);
    updateMeta('meta[name="twitter:description"]', homestay.meta_description || homestay.description);
    updateMeta('meta[name="twitter:image"]', ogImage);
    
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    const canonicalUrl = homestay.slug 
      ? `${window.location.origin}/homestay/${homestay.slug}`
      : currentUrl;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;
    
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
    
    // Schema.org structured data - Use Hotel or LodgingBusiness based on property type
    const schemaType = homestay.property_type === "Hotel" ? "Hotel" : "LodgingBusiness";
    const featuredImage = homestay.featured_image || homestay.og_image || displayImages[0];
    const featuredImageUrl = featuredImage?.startsWith('http') 
      ? featuredImage 
      : `${process.env.REACT_APP_BACKEND_URL}${featuredImage}`;
    
    const schemaData = {
      "@context": "https://schema.org",
      "@type": schemaType,
      "name": homestay.name,
      "description": homestay.meta_description || homestay.description || `${homestay.name} - A beautiful homestay offering comfortable accommodation.`,
      "image": [
        featuredImageUrl,
        ...displayImages.slice(0, 5).map(img => img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`)
      ].filter(Boolean),
      "address": {
        "@type": "PostalAddress",
        "streetAddress": homestay.address,
        "addressLocality": homestay.sub_location_name || homestay.location_name || "",
        "addressRegion": homestay.location_name || "",
        "addressCountry": "IN"
      },
      "telephone": homestay.mobile_number,
      "url": canonicalUrl,
      "priceRange": homestay.price_per_night 
        ? `${homestay.price_currency || 'INR'} ${homestay.price_per_night}` 
        : homestay.price_per_person
        ? `${homestay.price_currency || 'INR'} ${homestay.price_per_person} per person`
        : "Contact for pricing",
      "checkinTime": homestay.check_in_time || "14:00",
      "checkoutTime": homestay.check_out_time || "11:00",
      "numberOfRooms": homestay.facilities?.includes("Multiple Rooms") ? 5 : undefined,
      "petsAllowed": homestay.facilities?.includes("Pet Friendly") ? true : undefined,
      "starRating": homestay.average_rating > 0 ? {
        "@type": "Rating",
        "ratingValue": homestay.average_rating.toFixed(1)
      } : undefined
    };
    
    // Add website if available
    if (homestay.website) {
      schemaData.sameAs = [homestay.website];
    }
    
    // Build offers array for both pricing types
    const offers = [];
    if (homestay.price_per_night) {
      offers.push({
        "@type": "Offer",
        "name": "Per Night Stay",
        "price": homestay.price_per_night,
        "priceCurrency": homestay.price_currency || "INR",
        "availability": "https://schema.org/InStock",
        "validFrom": new Date().toISOString().split('T')[0]
      });
    }
    if (homestay.price_per_person) {
      const mealNames = {
        breakfast: "Breakfast", lunch: "Lunch", high_tea: "High Tea",
        dinner: "Dinner", welcome_drink: "Welcome Drink", evening_snacks: "Evening Snacks",
        packed_lunch: "Packed Lunch", bbq: "BBQ", veg_only: "Veg Menu",
        veg_nonveg: "Veg & Non-Veg", seafood: "Seafood", local_cuisine: "Local Konkani Cuisine"
      };
      const mealList = (homestay.meal_packages || []).map(p => mealNames[p] || p).join(", ");
      offers.push({
        "@type": "Offer",
        "name": "Per Person Package (includes meals)",
        "description": mealList
          ? `Saturday morning to Sunday morning package including ${mealList}`
          : "Saturday morning to Sunday morning package including lunch, high tea, dinner and breakfast",
        "price": homestay.price_per_person,
        "priceCurrency": homestay.price_currency || "INR",
        "availability": "https://schema.org/InStock",
        "validFrom": new Date().toISOString().split('T')[0]
      });
    }
    if (offers.length === 1) {
      schemaData.offers = offers[0];
    } else if (offers.length > 1) {
      schemaData.offers = offers;
    }
    
    if (homestay.average_rating > 0) {
      schemaData.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": homestay.average_rating.toFixed(1),
        "reviewCount": homestay.review_count,
        "bestRating": "5",
        "worstRating": "1"
      };
    }
    
    if (homestay.latitude && homestay.longitude) {
      schemaData.geo = {
        "@type": "GeoCoordinates",
        "latitude": homestay.latitude,
        "longitude": homestay.longitude
      };
    }
    
    if (homestay.facilities?.length > 0) {
      schemaData.amenityFeature = homestay.facilities.map(facility => ({
        "@type": "LocationFeatureSpecification",
        "name": facility,
        "value": true
      }));
    }
    
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin },
        { "@type": "ListItem", "position": 2, "name": "Listings", "item": `${window.location.origin}/listings` },
        { "@type": "ListItem", "position": 3, "name": homestay.name, "item": currentUrl }
      ]
    };
    
    document.querySelectorAll('script[data-schema]').forEach(el => el.remove());
    
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.setAttribute('data-schema', 'lodging');
    schemaScript.textContent = JSON.stringify(schemaData);
    document.head.appendChild(schemaScript);
    
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.setAttribute('data-schema', 'breadcrumb');
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);
    
    return () => {
      document.querySelectorAll('script[data-schema]').forEach(el => el.remove());
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) canonicalLink.remove();
    };
  }, [homestay, reviews]);

  const fetchHomestay = async () => {
    setLoading(true);
    try {
      let response;
      if (slug) {
        response = await homestayAPI.getBySlug(slug);
      } else {
        response = await homestayAPI.getOne(id);
      }
      setHomestay(response.data);
      // Track view (fire-and-forget) - only once per page visit
      const hid = response.data?.id;
      if (hid && !viewTrackedRef.current) {
        viewTrackedRef.current = true;
        fetch(`${BACKEND_URL}/api/homestays/${hid}/track-view`, { method: "POST" }).catch(() => {});
      }
    } catch (error) {
      console.error("Error fetching homestay:", error);
      
      // If homestay not found, check for redirect
      if (error.response?.status === 404 && slug) {
        try {
          const redirectCheck = await fetch(`${BACKEND_URL}/api/homestays/check-redirect/${slug}`);
          const redirectData = await redirectCheck.json();
          
          if (redirectData.has_redirect && redirectData.destination_url) {
            // Redirect to new URL
            window.location.href = redirectData.destination_url;
            return;
          }
        } catch (redirectError) {
          console.error("Error checking redirect:", redirectError);
        }
      }
      
      toast.error("Failed to load homestay details");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const homestayId = homestay?.id || id;
      const response = await reviewAPI.getAll({ homestay_id: homestayId, approved_only: true });
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const fetchNearbyPlaces = async () => {
    if (!homestay?.latitude || !homestay?.longitude) return;
    setLoadingPlaces(true);
    try {
      const response = await placeAPI.getNearby({
        latitude: homestay.latitude,
        longitude: homestay.longitude,
        radius_km: 50,
        limit: 10
      });
      setNearbyPlaces(response.data);
    } catch (error) {
      console.error("Error fetching nearby places:", error);
    } finally {
      setLoadingPlaces(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${BACKEND_URL}${imagePath}`;
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryForm.name || !inquiryForm.email || !inquiryForm.phone || !inquiryForm.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmittingInquiry(true);
    try {
      await inquiryAPI.create({
        homestay_id: homestay.id,
        ...inquiryForm,
        check_in_date: checkInDate ? format(checkInDate, "yyyy-MM-dd") : null,
        check_out_date: checkOutDate ? format(checkOutDate, "yyyy-MM-dd") : null,
      });
      toast.success("Inquiry sent successfully! We'll get back to you soon.");
      setInquiryForm({ name: "", email: "", phone: "", message: "", guests: 2 });
      setCheckInDate(null);
      setCheckOutDate(null);
    } catch (error) {
      toast.error("Failed to send inquiry. Please try again.");
    } finally {
      setSubmittingInquiry(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewForm.reviewer_name || !reviewForm.reviewer_email || !reviewForm.comment) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmittingReview(true);
    try {
      await reviewAPI.create({
        homestay_id: homestay.id,
        ...reviewForm,
      });
      toast.success("Review submitted! It will be visible after approval.");
      setReviewForm({ reviewer_name: "", reviewer_email: "", rating: 5, comment: "" });
    } catch (error) {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (!homestay) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="listing-not-found">
        <Navbar />
        <div className="pt-32 pb-20 px-4 md:px-8 lg:px-12 text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
            <Home className="w-10 h-10 text-muted-foreground" />
          </div>
          <h1
            className="text-3xl md:text-4xl font-semibold text-foreground mb-4"
           
          >
            Homestay Not Found
          </h1>
          <p className="text-muted-foreground mb-8">
            The property you&apos;re looking for doesn&apos;t exist or may have been removed.
          </p>
          <Link to="/listings">
            <Button className="rounded-full px-8 h-12">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Browse All Listings
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Build display images array with featured image first
  const buildDisplayImages = () => {
    if (!homestay.images?.length) {
      return ["https://images.unsplash.com/photo-1664616466598-1f1a394f9009?w=1200&h=800&fit=crop"];
    }
    
    // If featured_image is set, put it first
    if (homestay.featured_image && homestay.images.includes(homestay.featured_image)) {
      const otherImages = homestay.images.filter(img => img !== homestay.featured_image);
      return [homestay.featured_image, ...otherImages];
    }
    
    return homestay.images;
  };
  
  const displayImages = buildDisplayImages();

  const ogImage = homestay.og_image || getImageUrl(displayImages[0]) || displayImages[0];
  const currentUrl = `${window.location.origin}${location.pathname}`;
  
  // Check if any booking links exist
  const hasBookingLinks = homestay.makemytrip_url || homestay.goibibo_url || 
    homestay.booking_com_url || homestay.agoda_url || homestay.airbnb_url || homestay.other_booking_url;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="listing-detail-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{homestay.meta_title || `${homestay.name}`}</title>
        <meta name="description" content={homestay.meta_description || homestay.description || `Book ${homestay.name}, a beautiful homestay with modern amenities.`} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={homestay.meta_title || `${homestay.name}`} />
        <meta property="og:description" content={homestay.meta_description || homestay.description || `Book ${homestay.name}, a beautiful homestay.`} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="Kokan Homestays" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={homestay.meta_title || `${homestay.name}`} />
        <meta name="twitter:description" content={homestay.meta_description || homestay.description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="canonical" href={homestay.slug ? `${window.location.origin}/homestay/${homestay.slug}` : currentUrl} />
      </Helmet>

      <Navbar />
      {homestay && <PageSEO title={homestay.meta_title || homestay.name} description={homestay.meta_description || homestay.description?.slice(0, 160)} path={`/homestay/${homestay.slug}`} image={ogImage} canonicalUrl={homestay.canonical_url} />}

      {/* Hero Section */}
      <section className="pt-20 lg:pt-24" data-testid="listing-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          
          {/* Breadcrumb & Actions */}
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/listings"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group"
              data-testid="back-to-listings"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium">Back to Listings</span>
            </Link>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full h-10 w-10 p-0 hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-all"
                onClick={() => setIsFavorite(!isFavorite)}
                data-testid="favorite-btn"
              >
                <Heart className={`w-4 h-4 transition-all ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
              </Button>
              <ShareButtons 
                url={homestay.slug ? `${window.location.origin}/homestay/${homestay.slug}` : window.location.href}
                title={homestay.name}
                description={homestay.meta_description || `${homestay.name} - ${homestay.address}`}
                variant="dropdown"
              />
            </div>
          </div>

          {/* Property Title & Quick Info */}
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {homestay.is_featured && (
                <Badge className="bg-accent text-white border-0 px-3 py-1">Featured</Badge>
              )}
              {homestay.average_rating > 0 && (
                <div className="flex items-center gap-1.5 bg-primary/5 px-3 py-1.5 rounded-full">
                  <Star className="w-4 h-4 fill-accent text-accent" />
                  <span className="font-semibold text-sm">{homestay.average_rating.toFixed(1)}</span>
                  <span className="text-muted-foreground text-sm">({homestay.review_count} reviews)</span>
                </div>
              )}
            </div>

            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-3 leading-tight"
             
              data-testid="listing-title"
            >
              {homestay.name}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                <span className="text-sm">{homestay.address}</span>
              </div>
              {homestay.location_name && (
                <span className="text-sm px-2 py-0.5 bg-secondary rounded-full">{homestay.location_name}</span>
              )}
            </div>
          </div>

          {/* Bento Image Grid */}
          <div className="relative" data-testid="listing-images">
            <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[320px] md:h-[400px] lg:h-[480px] rounded-2xl overflow-hidden" data-testid="image-grid">
              {/* Main Image */}
              <div
                className="col-span-2 row-span-2 relative cursor-pointer group"
                onClick={() => setSelectedImage(0)}
              >
                <img
                  src={getImageUrl(displayImages[0]) || displayImages[0]}
                  alt={`${homestay.name} - Main`}
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  loading="eager"
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1664616466598-1f1a394f9009?w=800&h=450&fit=crop";
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </div>

              {/* Secondary Images */}
              {[1, 2, 3, 4].map((index) => (
                <div
                  key={index}
                  className="relative cursor-pointer group overflow-hidden"
                  onClick={() => displayImages[index] && setSelectedImage(index)}
                >
                  {displayImages[index] ? (
                    <>
                      <img
                        src={getImageUrl(displayImages[index]) || displayImages[index]}
                        alt={`${homestay.name} ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1664616466598-1f1a394f9009?w=800&h=450&fit=crop";
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                      
                      {/* Show more overlay on last visible image */}
                      {index === 4 && displayImages.length > 5 && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="text-white text-center">
                            <Images className="w-6 h-6 mx-auto mb-1" />
                            <span className="text-sm font-medium">+{displayImages.length - 5} more</span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">No image</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Gallery Actions */}
            <div className="absolute bottom-4 right-4 flex items-center gap-2">
              {homestay.video && (
                <Button
                  onClick={() => setShowVideoModal(true)}
                  className="bg-white/90 hover:bg-white text-foreground shadow-lg rounded-full"
                  size="sm"
                  data-testid="play-video-btn"
                >
                  <Play className="w-4 h-4 mr-2 fill-current" />
                  Play Video
                </Button>
              )}
              <Button
                onClick={() => setSelectedImage(0)}
                variant="secondary"
                className="bg-white/90 hover:bg-white text-foreground shadow-lg rounded-full"
                size="sm"
              >
                <Images className="w-4 h-4 mr-2" />
                {displayImages.length} Photos
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* Left Column - Main Content */}
            <div className="lg:col-span-8 space-y-10">
              
              {/* Price & Contact Bar */}
              <div className="flex flex-wrap items-start justify-between gap-6 pb-8 border-b border-border">
                {/* Price Display */}
                {(homestay.price_per_night || homestay.price_per_person) && (
                  <div data-testid="listing-price">
                    {homestay.price_per_night && (
                      <div className="mb-2">
                        <p className="text-sm text-muted-foreground mb-1">Starting from</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl md:text-4xl font-semibold text-foreground">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: homestay.price_currency || 'INR',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(homestay.price_per_night)}
                          </span>
                          <span className="text-muted-foreground text-lg">/ night</span>
                        </div>
                      </div>
                    )}
                    {homestay.price_per_person && (
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-xl font-semibold text-[#C86A3A]">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: homestay.price_currency || 'INR',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(homestay.price_per_person)}
                        </span>
                        <span className="text-muted-foreground text-sm">/ person</span>
                        <span className="text-xs text-muted-foreground ml-1">(incl. meals)</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Contact Links */}
                <div className="flex flex-wrap items-center gap-4">
                  <a
                    href={`tel:${homestay.mobile_number}`}
                    onClick={() => trackCallClick(homestay.id)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors"
                    data-testid="listing-phone"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="font-medium">{homestay.mobile_number}</span>
                  </a>
                  {homestay.website && (
                    <a
                      href={homestay.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 border border-border rounded-full hover:bg-secondary transition-colors"
                      data-testid="listing-website"
                    >
                      <Globe className="w-4 h-4" />
                      <span className="font-medium">Website</span>
                    </a>
                  )}
                </div>
              </div>

              {/* Check-in/Check-out Times */}
              <div>
                <h2
                  className="text-2xl font-semibold text-foreground mb-6"
                 
                >
                  Check-in & Check-out
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div 
                    className="flex items-center gap-4 p-5 bg-emerald-50 border border-emerald-100 rounded-xl"
                    data-testid="checkin-time-display"
                  >
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-emerald-600 font-medium">Check-in</p>
                      <p className="text-2xl font-semibold text-foreground">
                        {homestay.check_in_time || "14:00"}
                      </p>
                    </div>
                  </div>
                  <div 
                    className="flex items-center gap-4 p-5 bg-amber-50 border border-amber-100 rounded-xl"
                    data-testid="checkout-time-display"
                  >
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-amber-600 font-medium">Check-out</p>
                      <p className="text-2xl font-semibold text-foreground">
                        {homestay.check_out_time || "11:00"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Meal Info */}
              {(homestay.meal_included || homestay.breakfast_included || (homestay.meal_packages && homestay.meal_packages.length > 0)) && (
                <div data-testid="meal-info-section">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Meal Package</h2>
                  
                  {/* Quick badges */}
                  <div className="flex flex-wrap gap-3 mb-4">
                    {homestay.meal_included && (
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/5 border border-primary/10 rounded-xl">
                        <Utensils className="w-4 h-4 text-primary" />
                        <span className="font-medium text-foreground text-sm">Meals Included</span>
                      </div>
                    )}
                    {homestay.breakfast_included && (
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/5 border border-accent/10 rounded-xl">
                        <Coffee className="w-4 h-4 text-accent" />
                        <span className="font-medium text-foreground text-sm">Breakfast Included</span>
                      </div>
                    )}
                  </div>

                  {/* Detailed meal packages */}
                  {homestay.meal_packages && homestay.meal_packages.length > 0 && (() => {
                    const mealLabels = {
                      breakfast: { label: "Breakfast", icon: "☀️", desc: "Morning breakfast" },
                      lunch: { label: "Lunch", icon: "🍛", desc: "Afternoon lunch" },
                      high_tea: { label: "High Tea / Snacks", icon: "🍵", desc: "Evening tea & snacks" },
                      dinner: { label: "Dinner", icon: "🍽️", desc: "Night dinner" },
                      welcome_drink: { label: "Welcome Drink", icon: "🥤", desc: "On arrival" },
                      evening_snacks: { label: "Evening Snacks", icon: "🥜", desc: "Light evening bites" },
                      packed_lunch: { label: "Packed Lunch", icon: "🧳", desc: "For excursions" },
                      bbq: { label: "BBQ / Bonfire Snacks", icon: "🔥", desc: "Open-air dining" },
                      veg_only: { label: "Veg Only", icon: "🥗", desc: "Vegetarian menu" },
                      veg_nonveg: { label: "Veg & Non-Veg", icon: "🍗", desc: "Both options available" },
                      seafood: { label: "Seafood Special", icon: "🦐", desc: "Fresh local catch" },
                      local_cuisine: { label: "Local Konkani Cuisine", icon: "🫕", desc: "Authentic regional dishes" },
                    };
                    return (
                      <div className="bg-[#FAF8F3] rounded-2xl border border-[#e8e0d0] p-5">
                        <p className="text-sm font-medium text-muted-foreground mb-3">What's included in the package:</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {homestay.meal_packages.map(pkg => {
                            const info = mealLabels[pkg] || { label: pkg, icon: "🍴", desc: "" };
                            return (
                              <div key={pkg} className="flex items-center gap-2.5 bg-white rounded-xl px-3 py-2.5 border border-gray-100" data-testid={`meal-detail-${pkg}`}>
                                <span className="text-lg">{info.icon}</span>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{info.label}</p>
                                  {info.desc && <p className="text-xs text-gray-500">{info.desc}</p>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {homestay.price_per_person && (
                          <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-[#e8e0d0]">
                            All meals above are included in the per-person package at {new Intl.NumberFormat('en-IN', {
                              style: 'currency', currency: homestay.price_currency || 'INR',
                              minimumFractionDigits: 0, maximumFractionDigits: 0,
                            }).format(homestay.price_per_person)}/person (Sat morning to Sun morning)
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Description */}
              {homestay.description && (
                <div>
                  <h2
                    className="text-2xl font-semibold text-foreground mb-4"
                   
                  >
                    About This Place
                  </h2>
                  <div className="prose prose-stone max-w-none">
                    <p className="text-muted-foreground leading-relaxed whitespace-pre-line text-base">
                      {homestay.description}
                    </p>
                  </div>
                </div>
              )}

              {/* Facilities & Amenities */}
              {homestay.facilities?.length > 0 && (
                <div>
                  <h2
                    className="text-2xl font-semibold text-foreground mb-6"
                   
                  >
                    Facilities & Amenities
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {homestay.facilities.map((facility, index) => {
                      const IconComponent = facilityIcons[facility] || facilityIcons.default;
                      return (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-4 bg-secondary/50 rounded-xl hover:bg-secondary transition-colors"
                        >
                          <IconComponent className="w-5 h-5 text-primary flex-shrink-0" />
                          <span className="text-sm font-medium text-foreground">{facility}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

{/* Book on Partner Sites section moved to sidebar */}

              {/* Location Map */}
              {homestay.latitude && homestay.longitude && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2
                      className="text-2xl font-semibold text-foreground"
                     
                    >
                      Location
                    </h2>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${homestay.latitude},${homestay.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors text-sm font-medium"
                      data-testid="get-directions-btn"
                    >
                      <Navigation className="w-4 h-4" />
                      Get Directions
                    </a>
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-border" data-testid="listing-map">
                    <iframe
                      width="100%"
                      height="350"
                      frameBorder="0"
                      scrolling="no"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${homestay.longitude - 0.01}%2C${homestay.latitude - 0.01}%2C${homestay.longitude + 0.01}%2C${homestay.latitude + 0.01}&layer=mapnik&marker=${homestay.latitude}%2C${homestay.longitude}`}
                      title="Property Location Map"
                      className="w-full"
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>{homestay.address}</span>
                    </div>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${homestay.latitude},${homestay.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                      data-testid="view-on-maps-link"
                    >
                      View on Google Maps
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}

              {/* Reviews Section */}
              <div data-testid="reviews-section">
                <div className="flex items-center justify-between mb-6">
                  <h2
                    className="text-2xl font-semibold text-foreground"
                   
                  >
                    Guest Reviews
                  </h2>
                  {homestay.average_rating > 0 && (
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 fill-accent text-accent" />
                      <span className="font-semibold">{homestay.average_rating.toFixed(1)}</span>
                      <span className="text-muted-foreground">({reviews.length} reviews)</span>
                    </div>
                  )}
                </div>

                {reviews.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2 mb-8">
                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        className="p-5 bg-secondary/30 rounded-xl"
                        data-testid={`review-${review.id}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary font-semibold">
                                {review.reviewer_name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium text-foreground">{review.reviewer_name}</span>
                          </div>
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < review.rating ? "fill-accent text-accent" : "text-muted"
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground mb-8 p-6 bg-secondary/30 rounded-xl text-center">
                    No reviews yet. Be the first to share your experience!
                  </p>
                )}

                {/* Review Form */}
                <div className="p-6 bg-white border border-border rounded-2xl shadow-soft">
                  <h3 className="text-lg font-semibold text-foreground mb-5">Write a Review</h3>
                  <form onSubmit={handleReviewSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        placeholder="Your Name *"
                        value={reviewForm.reviewer_name}
                        onChange={(e) => setReviewForm({ ...reviewForm, reviewer_name: e.target.value })}
                        className="h-12 rounded-xl border-border"
                        data-testid="review-name-input"
                      />
                      <Input
                        type="email"
                        placeholder="Your Email *"
                        value={reviewForm.reviewer_email}
                        onChange={(e) => setReviewForm({ ...reviewForm, reviewer_email: e.target.value })}
                        className="h-12 rounded-xl border-border"
                        data-testid="review-email-input"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground mb-3 block">Your Rating</label>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                            className="p-1 hover:scale-110 transition-transform"
                            data-testid={`rating-star-${star}`}
                          >
                            <Star
                              className={`w-8 h-8 transition-colors ${
                                star <= reviewForm.rating
                                  ? "fill-accent text-accent"
                                  : "text-muted hover:text-accent/50"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    <Textarea
                      placeholder="Share your experience with this homestay... *"
                      value={reviewForm.comment}
                      onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                      rows={4}
                      className="rounded-xl border-border resize-none"
                      data-testid="review-comment-input"
                    />

                    <Button
                      type="submit"
                      disabled={submittingReview}
                      className="rounded-full px-8 h-12"
                      data-testid="submit-review-btn"
                    >
                      {submittingReview ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Submit Review
                    </Button>
                  </form>
                </div>
              </div>
            </div>

            {/* Right Column - Sticky Booking Sidebar */}
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-24" data-testid="inquiry-form">
                <div className="bg-white border border-border rounded-2xl shadow-floating p-6">
                  <h2
                    className="text-xl font-semibold text-foreground mb-6"
                   
                  >
                    Book Your Stay
                  </h2>

                  <form onSubmit={handleInquirySubmit} className="space-y-4">
                    <Input
                      placeholder="Your Name *"
                      value={inquiryForm.name}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                      className="h-12 rounded-xl border-border"
                      data-testid="inquiry-name-input"
                    />

                    <Input
                      type="email"
                      placeholder="Email Address *"
                      value={inquiryForm.email}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                      className="h-12 rounded-xl border-border"
                      data-testid="inquiry-email-input"
                    />

                    <Input
                      type="tel"
                      placeholder="Phone Number *"
                      value={inquiryForm.phone}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                      className="h-12 rounded-xl border-border"
                      data-testid="inquiry-phone-input"
                    />

                    {/* Date Pickers */}
                    <div className="grid grid-cols-2 gap-3">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="h-12 justify-start text-left font-normal rounded-xl border-border"
                            data-testid="checkin-date-btn"
                          >
                            <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                            <span className={checkInDate ? "text-foreground" : "text-muted-foreground"}>
                              {checkInDate ? format(checkInDate, "MMM d") : "Check-in"}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={checkInDate}
                            onSelect={setCheckInDate}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="h-12 justify-start text-left font-normal rounded-xl border-border"
                            data-testid="checkout-date-btn"
                          >
                            <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                            <span className={checkOutDate ? "text-foreground" : "text-muted-foreground"}>
                              {checkOutDate ? format(checkOutDate, "MMM d") : "Check-out"}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={checkOutDate}
                            onSelect={setCheckOutDate}
                            disabled={(date) => date < (checkInDate || new Date())}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Guests */}
                    <div className="flex items-center gap-3 h-12 px-3 border border-border rounded-xl">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        value={inquiryForm.guests}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, guests: parseInt(e.target.value) || 1 })}
                        className="h-10 border-0 p-0 focus-visible:ring-0"
                        data-testid="inquiry-guests-input"
                      />
                      <span className="text-sm text-muted-foreground">guests</span>
                    </div>

                    <Textarea
                      placeholder="Your Message *"
                      value={inquiryForm.message}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                      rows={3}
                      className="rounded-xl border-border resize-none"
                      data-testid="inquiry-message-input"
                    />

                    <Button
                      type="submit"
                      disabled={submittingInquiry}
                      className="w-full h-14 bg-accent hover:bg-accent/90 text-white rounded-full text-base font-semibold"
                      data-testid="submit-inquiry-btn"
                    >
                      {submittingInquiry ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Send className="w-5 h-5 mr-2" />
                      )}
                      Send Inquiry
                    </Button>
                  </form>

                  <Separator className="my-6" />

                  {/* Direct Contact */}
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-3">Or contact directly</p>
                    <a
                      href={`tel:${homestay.mobile_number}`}
                      onClick={() => trackCallClick(homestay.id)}
                      className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
                      data-testid="direct-contact-link"
                    >
                      <Phone className="w-4 h-4" />
                      {homestay.mobile_number}
                    </a>
                  </div>
                </div>

                {/* Book on Partner Sites - In Sidebar */}
                {hasBookingLinks && (
                  <div className="mt-6 bg-white border border-border rounded-2xl shadow-soft p-5" data-testid="booking-partners-sidebar">
                    <h3
                      className="text-base font-semibold text-foreground mb-4"
                     
                    >
                      Book on Partner Sites
                    </h3>
                    <div className="space-y-2.5">
                      {homestay.makemytrip_url && (
                        <PartnerBookingLink
                          href={homestay.makemytrip_url}
                          name="MakeMyTrip"
                          bgColor="bg-[#0770E3]"
                          testId="booking-makemytrip"
                        />
                      )}
                      {homestay.goibibo_url && (
                        <PartnerBookingLink
                          href={homestay.goibibo_url}
                          name="Goibibo"
                          bgColor="bg-[#EC5B24]"
                          testId="booking-goibibo"
                        />
                      )}
                      {homestay.booking_com_url && (
                        <PartnerBookingLink
                          href={homestay.booking_com_url}
                          name="Booking.com"
                          bgColor="bg-[#003580]"
                          testId="booking-bookingcom"
                        />
                      )}
                      {homestay.agoda_url && (
                        <PartnerBookingLink
                          href={homestay.agoda_url}
                          name="Agoda"
                          bgColor="bg-[#5C2D91]"
                          testId="booking-agoda"
                        />
                      )}
                      {homestay.airbnb_url && (
                        <PartnerBookingLink
                          href={homestay.airbnb_url}
                          name="Airbnb"
                          bgColor="bg-[#FF5A5F]"
                          testId="booking-airbnb"
                        />
                      )}
                      {homestay.other_booking_url && (
                        <PartnerBookingLink
                          href={homestay.other_booking_url}
                          name="Other Booking"
                          bgColor="bg-gray-700"
                          testId="booking-other"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby Places Section */}
      {nearbyPlaces.length > 0 && (
        <section className="py-16 bg-white" data-testid="nearby-places-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Compass className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-primary uppercase tracking-wider">
                    Nearby Attractions
                  </span>
                </div>
                <h2
                  className="text-3xl md:text-4xl font-semibold text-foreground"
                 
                >
                  Places to Explore
                </h2>
                <p className="text-muted-foreground mt-2">
                  Discover interesting places within 50km of this homestay
                </p>
              </div>
              <Link to="/places" className="hidden md:block">
                <Button variant="outline" className="rounded-full">
                  View All Places
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            {loadingPlaces ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {nearbyPlaces.slice(0, 8).map((place) => (
                  <NearbyPlaceCard key={place.id} place={place} />
                ))}
              </div>
            )}

            <div className="md:hidden text-center mt-8">
              <Link to="/places">
                <Button variant="outline" className="rounded-full">
                  View All Places
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Image Lightbox Dialog */}
      <Dialog open={selectedImage !== null} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl p-0 bg-black/95 border-0">
          <div className="relative">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {selectedImage !== null && (
              <div className="relative">
                <img
                  src={getImageUrl(displayImages[selectedImage]) || displayImages[selectedImage]}
                  alt={`${homestay.name} ${selectedImage + 1}`}
                  className="w-full h-auto max-h-[80vh] object-contain"
                  loading="lazy"
                />
                
                {/* Navigation */}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-4">
                  <button
                    onClick={() => setSelectedImage((prev) => (prev - 1 + displayImages.length) % displayImages.length)}
                    className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <span className="text-white bg-black/50 px-4 py-2 rounded-full text-sm">
                    {selectedImage + 1} / {displayImages.length}
                  </span>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev + 1) % displayImages.length)}
                    className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
            
            {/* Thumbnail Strip */}
            <div className="p-4 flex gap-2 overflow-x-auto">
              {displayImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 transition-all ${
                    selectedImage === index 
                      ? "ring-2 ring-white scale-105" 
                      : "opacity-50 hover:opacity-80"
                  }`}
                >
                  <img
                    src={getImageUrl(image) || image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Modal */}
      {showVideoModal && homestay.video && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(false)}
          data-testid="video-modal"
        >
          <button
            onClick={() => setShowVideoModal(false)}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <video
            src={`${BACKEND_URL}${homestay.video}`}
            controls
            autoPlay
            className="max-w-full max-h-[80vh] rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

// Partner Booking Link Component with Real Logos
const PartnerBookingLink = ({ href, name, bgColor, testId }) => {
  // Brand abbreviations as fallback
  const getBrandAbbr = (brandName) => {
    const abbrs = {
      'MakeMyTrip': 'MMT',
      'Goibibo': 'goibibo',
      'Booking.com': 'Booking.com',
      'Agoda': 'agoda',
      'Airbnb': 'airbnb',
      'Other Booking': 'Book'
    };
    return abbrs[brandName] || brandName.substring(0, 3);
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`group flex items-center gap-3 px-4 py-3.5 ${bgColor} text-white rounded-xl transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:-translate-y-0.5`}
      data-testid={testId}
    >
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <span className="font-bold text-sm text-white whitespace-nowrap">{getBrandAbbr(name)}</span>
      </div>
      <ExternalLink className="w-4 h-4 opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </a>
  );
};

// Category icons for places
const categoryIcons = {
  Restaurants: UtensilsCrossed,
  Temples: Home,
  Beaches: Waves,
  Waterfalls: Waves,
  Forts: Mountain,
  Parks: TreePine,
};

// Nearby Place Card Component
const NearbyPlaceCard = ({ place }) => {
  const imageUrl = place.image?.startsWith('/') 
    ? `${BACKEND_URL}${place.image}` 
    : place.image;
  
  const IconComponent = categoryIcons[place.category] || Compass;

  return (
    <Link 
      to={`/place/${place.slug || place.id}`}
      className="bg-[#FDFBF7] rounded-xl overflow-hidden group hover:shadow-soft transition-all duration-300 block"
    >
      {/* Image */}
      <div className="aspect-video bg-muted relative overflow-hidden">
        {place.image ? (
          <img
            src={imageUrl}
            alt={place.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
            <IconComponent className="w-10 h-10 text-primary/30" />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/90 text-xs font-medium rounded-full">
            <IconComponent className="w-3 h-3" />
            {place.category}
          </span>
        </div>
        {place.distance_km && (
          <div className="absolute bottom-2 right-2">
            <span className="px-2.5 py-1 bg-black/70 text-white text-xs rounded-full">
              {place.distance_km.toFixed(1)} km
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h4 className="font-semibold text-foreground mb-1.5 line-clamp-1 group-hover:text-primary transition-colors">
          {place.name}
        </h4>
        {place.description && (
          <p 
            className="text-sm text-muted-foreground line-clamp-2 mb-3"
            dangerouslySetInnerHTML={{ __html: place.description.replace(/<[^>]*>/g, '').slice(0, 80) }}
          />
        )}
        <span className="inline-flex items-center gap-1 text-sm text-primary font-medium group-hover:gap-2 transition-all">
          View details
          <ChevronRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
};

export default ListingDetailPage;
